import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Sparkle, Notebook, Calendar, PencilSimple } from "@phosphor-icons/react";
import { BILLING_MODEL_LABEL } from "@/lib/enumLabels";

const TYPES = [
  { value: "trusted_advisor", label: "Trusted Advisor" },
  { value: "strategic_initiative", label: "Strategic Initiative" },
  { value: "executive_convening", label: "Executive Convening" },
  { value: "executive_communication", label: "Executive Comms & Facilitation" },
];
const TYPE_LABEL = TYPES.reduce((acc, t) => { acc[t.value] = t.label; return acc; }, {});

/**
 * Engagement page header — breadcrumb, title, type/billing pill,
 * and the three top-level action buttons. Purely presentational; parent
 * owns the open/close state for the dialogs.
 *
 * Iter 85.62 · Two additions:
 *   • Contextual Back button uses browser history when available;
 *     falls back to /engagements when the partner deep-linked in.
 *   • Engagement name is inline-editable — click the pencil to swap
 *     the h1 for an input, Enter/blur to save, Escape to cancel. The
 *     underlying canonical Engagement.id is untouched so every
 *     relationship (tasks, notes, timesheets, invoices) survives.
 */
export default function EngagementHeader({ engagement: e, client, onAskAi, onDiagnostic, onCloseout, onWeeklyBrief, onRename }) {
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(e.name || "");
  useEffect(() => { setNameInput(e.name || ""); }, [e.name]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/engagements");
  };

  const commitName = async () => {
    const next = nameInput.trim();
    if (!next || next === e.name) { setEditingName(false); setNameInput(e.name || ""); return; }
    if (onRename) await onRename(next);
    setEditingName(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={goBack}
        data-testid="engagement-back-btn"
        className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={12} /> Back
      </button>
      <div className="flex flex-col md:flex-row items-start md:justify-between gap-3 mb-6">
        <div className="min-w-0 flex-1">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">
            {TYPE_LABEL[e.engagement_type] || e.engagement_type} · {BILLING_MODEL_LABEL[e.billing_model] || e.billing_model}
          </div>
          {editingName ? (
            <Input
              autoFocus
              value={nameInput}
              onChange={(ev) => setNameInput(ev.target.value)}
              onBlur={commitName}
              onKeyDown={(ev) => {
                if (ev.key === "Enter") commitName();
                if (ev.key === "Escape") { setEditingName(false); setNameInput(e.name || ""); }
              }}
              data-testid="engagement-name-input"
              className="mt-2 font-display text-2xl md:text-4xl font-bold tracking-tight rounded-none bg-transparent border-b border-primary/60 p-0 h-auto focus-visible:ring-0 focus-visible:border-primary"
            />
          ) : (
            <h1
              className="mt-2 font-display text-2xl md:text-4xl font-bold tracking-tight inline-flex items-center gap-2 group cursor-text"
              onClick={() => setEditingName(true)}
              data-testid="engagement-name-heading"
            >
              {e.name}
              <PencilSimple
                size={16}
                weight="bold"
                className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Rename engagement"
              />
            </h1>
          )}
          {client && (
            <div className="mt-2 text-sm">
              <Link to={`/clients/${client.id}`} className="text-primary hover:underline">{client.name}</Link>{" "}
              <span className="text-muted-foreground">· {client.industry || "—"}</span>
            </div>
          )}
        </div>
        <div className="flex flex-nowrap gap-2 shrink-0">
          <Button onClick={onAskAi} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 whitespace-nowrap shrink-0" data-testid="ai-on-engagement">
            <Sparkle size={13} weight="fill" className="mr-1.5" /> Ask YadidAi
          </Button>
          <Button onClick={onDiagnostic} variant="outline" className="rounded-none border-primary/60 text-primary hover:bg-primary/10 whitespace-nowrap shrink-0" data-testid="diagnostic-memo-btn">
            <Notebook size={13} weight="bold" className="mr-1.5" /> Diagnostic memo
          </Button>
          <Button onClick={onWeeklyBrief} variant="outline" className="rounded-none border-primary/60 text-primary hover:bg-primary/10 whitespace-nowrap shrink-0" data-testid="weekly-brief-btn">
            <Calendar size={13} weight="bold" className="mr-1.5" /> Weekly brief
          </Button>
          <Button onClick={onCloseout} variant="outline" className="rounded-none border-primary/60 text-primary hover:bg-primary/10 whitespace-nowrap shrink-0" data-testid="closeout-memo-btn">
            <Notebook size={13} weight="bold" className="mr-1.5" /> Closeout memo
          </Button>
        </div>
      </div>
    </>
  );
}
